import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Title,
  getCurrentWeekNumber,
  ReloadIconButton,
  FarmLocationSelectModal,
  WeekRangeSelector
} from '../../elements';
import { Skeleton } from '../../elements/ui/skeleton';
import { notifications, extractApiErrorMessage } from '../../utils';
import { AnimalType } from '../../models/animal';
import VaccinationAndMedicationTrendData from '../../components/VaccinationAndMedicationTrendData';
import VaccinationAndMedicationDataTable from '../../components/VaccinationAndMedicationDataTable';
import { FormControl, Grid, InputLabel, MenuItem, Select, TextField } from '@material-ui/core';
import LocalHospitalIcon from '@material-ui/icons/LocalHospital';
import { useStyles } from './styles';
import { useTranslation } from 'react-i18next';
import useAuthContext from '../../hooks/useAuthContext';
import { getDashboardConfigByFarmType } from '../../utils';
import { ApiService } from '../../hooks';
import {
  HealthSystemDomainInjectionsResultInjectionHistoryStrategyViewModel,
  InjectionsGetInjectionStrategyParams
} from '../../api/HealthSystemApi';
import { LocationOption } from '../../models/locations';

interface VaccinationAndMedicationProps {
  animalType: AnimalType;
  locationId?: string;
  hideLocationPicker?: boolean;
}

const VaccinationAndMedication: React.FC<any> = ({
  animalType,
  locationId,
  hideLocationPicker
}: VaccinationAndMedicationProps) => {
  const classes = useStyles();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { state } = useAuthContext();
  const apiService = new ApiService();

  const [injectionStrategy, setInjectionStrategy] =
    React.useState<HealthSystemDomainInjectionsResultInjectionHistoryStrategyViewModel | null>(null);
  const [isBusy, setIsBusy] = React.useState(false);
  const [loaded, setLoaded] = React.useState(false);

  const currentWeekNumber = getCurrentWeekNumber(state.culture) as number;
  const currentYear = new Date().getFullYear();
  const [year, setYear] = React.useState(currentYear);
  const [weekStart, setWeekStart] = React.useState(Math.max(1, currentWeekNumber - 10));
  const [weekEnd, setWeekEnd] = React.useState(currentWeekNumber);
  const [selectedLocation, setSelectedLocation] = React.useState<LocationOption | null>(null);

  const handleYearNumberChange = (year: number) => {
    setYear(year);
    setLoaded(false);
  };

  const handleLocationChange = (location: LocationOption | null) => {
    setSelectedLocation(location);
    setLoaded(false);
  };

  React.useEffect(() => {
    setLoaded(false);
  }, [animalType]);

  const [selectedAnimalType, setSelectedAnimalType] = React.useState(animalType);

  const loadInjectionStrategy = async () => {
    setIsBusy(true);
    setLoaded(false);

    apiService.api.injections
      .injectionsGetInjectionStrategy({
        animalType,
        weekStart,
        weekNumber: weekEnd,
        year,
        locationId: locationId ?? (selectedLocation ? selectedLocation.id : undefined)
      } as InjectionsGetInjectionStrategyParams)
      .then((result) => {
        setInjectionStrategy(result.data);
        setLoaded(true);
      })
      .catch((e: unknown) => {
        notifications.error(extractApiErrorMessage(e, { fallback: t('f-com-api-error') }));
      })
      .finally(() => setIsBusy(false));
  };

  const handleTabChange = (_event: React.ChangeEvent<object>, newValue: AnimalType) => {
    setSelectedAnimalType(newValue);
    if (!locationId) {
      navigate(`/strategy/injections/${newValue}`);
    }
  };

  React.useEffect(() => {
    if (!loaded) {
      const load = async () => loadInjectionStrategy();
      load();
    }
  }, [loaded]);

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} md={12}>
        <Grid
          container
          direction="row"
          alignItems="center"
          spacing={1}
          className={classes.vaccinationMedicationInfoBox}
        >
          {/* Vaccination & Medication title */}
          <Grid item container xs={10} spacing={1}>
            <Grid item>
              <LocalHospitalIcon color="primary" fontSize="large" />
            </Grid>
            <Grid item>
              <Title>{t('f-strat-vacc-title')}</Title>
            </Grid>
          </Grid>

          {/* reload icon button */}
          <Grid item xs={1} className={classes.refreshIconGrid}>
            <ReloadIconButton action={() => setLoaded(false)} />
          </Grid>
        </Grid>

        {/* Filters section */}
        <Grid item container alignItems="center" spacing={1}>
          <Grid item xs={2} sm={2} md={2}>
            <AnimalTypeTabs value={selectedAnimalType} handleTabChange={handleTabChange} />
          </Grid>

          <Grid item>
            <TextField
              variant="outlined"
              type="number"
              label={t('f-com-year')}
              size="small"
              value={year}
              onChange={(e) => {
                const newValue = Number(e.target.value);
                if (newValue >= currentYear - 20 && newValue <= currentYear) {
                  handleYearNumberChange(newValue);
                }
              }}
            />
          </Grid>

          {!hideLocationPicker && (
            <Grid item xs={2} sm={2} md={2}>
              <FarmLocationSelectModal
                disableAny={false}
                location={selectedLocation}
                fullWidth
                cssClass={classes.formControl}
                onChange={(location) => {
                  handleLocationChange(location);
                }}
              />
            </Grid>
          )}

          <Grid item>
            <WeekRangeSelector
              onChange={(start, end) => {
                setWeekStart(start);
                setWeekEnd(end);
                setLoaded(false);
              }}
              weeksBack={10}
              year={year}
              className="min-w-70"
            />
          </Grid>

          {/* Filters section */}

          {isBusy && (
            <Grid item xs={12}>
              <div className="rounded border border-gray-200 overflow-hidden">
                <div className="bg-gray-50 border-b border-gray-200 px-4 py-2 flex gap-4">
                  {[1, 2, 3, 4, 5].map((_, i) => (
                    <Skeleton key={i} className="h-4 flex-1" />
                  ))}
                </div>
                {[1, 2, 3, 4, 5].map((row) => (
                  <div key={row} className="flex gap-4 px-4 py-3 border-b border-gray-100 last:border-0">
                    {[1, 2, 3, 4, 5].map((_, i) => (
                      <Skeleton key={i} className="h-3.5 flex-1" />
                    ))}
                  </div>
                ))}
              </div>
            </Grid>
          )}

          {!isBusy && loaded && injectionStrategy && (
            <Grid item xs={12}>
              <VaccinationAndMedicationDataTable animalType={animalType} supplyInfo={injectionStrategy.supplyInfo} />
            </Grid>
          )}
        </Grid>
      </Grid>

      {/* trend data */}
      <Grid item xs={12}>
        <VaccinationAndMedicationTrendData
          handleTabChange={handleTabChange}
          year={year}
          animalType={animalType}
          weekStart={weekStart}
          weekEnd={weekEnd}
        />
      </Grid>
    </Grid>
  );
};

export default VaccinationAndMedication;

// animal type tabs (also used at Trend data section)
export interface AnimalTypeTabsProps {
  value: number;
  handleTabChange: (event: React.ChangeEvent<object>, newValue: AnimalType) => void;
}

export const AnimalTypeTabs: React.FC<any> = ({ value, handleTabChange }: AnimalTypeTabsProps) => {
  const { t } = useTranslation();
  const { state } = useAuthContext();

  const buildAnimalList = (): AnimalType[] => {
    const animalTypes = [] as AnimalType[];
    const config = getDashboardConfigByFarmType(state.farmType);

    if (config.displaySow) animalTypes.push(AnimalType.Sow);
    if (config.displayGilt) animalTypes.push(AnimalType.Gilt);
    if (config.displayPiglet) animalTypes.push(AnimalType.Piglet);
    if (config.displayFattening) animalTypes.push(AnimalType.Fattening);
    if (config.displayCow) animalTypes.push(AnimalType.Cow);
    if (config.displayGoat) animalTypes.push(AnimalType.Goat);
    if (config.displayCalf) animalTypes.push(AnimalType.Calf);
    animalTypes.push(AnimalType.Boar);

    return animalTypes;
  };

  const animalList = buildAnimalList();
  const resolvedValue = animalList.includes(value) ? value : (animalList[0] ?? value);

  return (
    <FormControl variant={'outlined'} size="small" fullWidth>
      <InputLabel id="supplyLabel">{t('f-com-animal-type')}</InputLabel>
      <Select
        label={t('f-com-animal-type')}
        labelId="supplyLabel"
        id="supplySelect"
        value={resolvedValue}
        onChange={(e) => handleTabChange(e, e.target.value as AnimalType)}
      >
        {animalList.map((value, index) => (
          <MenuItem key={index} value={value}>
            {t(AnimalType[value])}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};
